import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { API_PREFIX } from 'widget/consts/api';
import type { BaseResponse, ApiRequestParams } from 'widget/types/api';

export class BaseApiStore {
  private readonly _baseURL: string;
  private _token: string | null = null;

  constructor(baseURL: string = `${API_PREFIX}`) {
    this._baseURL = baseURL;
  }

  public setToken(token: string | null): void {
    this._token = token;
  }

  public getToken(): string | null {
    return this._token;
  }

  protected async api<
    ResponseData = unknown,
    ErrorCode extends string = string
  >({
    endpoint,
    params = {},
    mock
  }: {
    endpoint: string;
    params?: ApiRequestParams;
    mock?: ResponseData;
  }): Promise<BaseResponse<ResponseData, ErrorCode>> {
    if (mock !== undefined) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      return {
        isError: false,
        data: mock
      } as BaseResponse<ResponseData, ErrorCode>;
    }

    const {
      method = 'GET',
      data,
      formData,
      headers = {},
      signal,
      omitAuthToken
    } = params;

    const url = `${this._baseURL}${endpoint}`;
    const isFormData = formData instanceof FormData;

    const config: AxiosRequestConfig = {
      method,
      url,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(this._token && !omitAuthToken && { 'X-Auth-Token': this._token }),
        ...headers
      },
      withCredentials: true,
      signal,
      ...(isFormData
        ? { data: formData }
        : method === 'GET'
          ? { params: data }
          : { data })
    };

    try {
      const response = await axios(config);

      return {
        isError: false,
        data: response.data
      } as BaseResponse<ResponseData, ErrorCode>;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }

      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<{ code?: string }>;

        // eslint-disable-next-line no-console
        console.error('API Error:', {
          url,
          method,
          status: axiosError.response?.status,
          data: axiosError.response?.data
        });

        return {
          isError: true,
          data: axiosError.response?.data?.code || 'UNKNOWN_ERROR'
        } as BaseResponse<ResponseData, ErrorCode>;
      }

      return {
        isError: true
      } as BaseResponse<ResponseData, ErrorCode>;
    }
  }
}
